import type {
  ModelCapabilities,
  ModelCapabilityName,
} from "@/shared/domain/orgs";
import {
  Collapsible,
  Toggle,
  ToggleGroup,
} from "@/shared/foundation/primitives";

import {
  CAPABILITY_FLAGS,
  CAPABILITY_LABELS,
  capabilityState,
  defaultOptionLabel,
  withCapabilityState,
  type CapabilityState,
} from "./capabilityOverrides";
import styles from "./CapabilityOverridesEditor.module.css";

interface CapabilityOverridesEditorProps {
  /** The flags an admin set; the rest follow the model family. */
  value: Partial<ModelCapabilities>;
  onChange: (next: Partial<ModelCapabilities>) => void;
  /** Effective capabilities, when known, to name each flag's default. */
  effective?: ModelCapabilities;
  /** Detected model family; `undefined` when not known yet (e.g. a new model). */
  family?: string | null;
  /** Accessible name of the section's toggle; "Capabilities" by default. */
  triggerLabel?: string;
  disabled?: boolean;
}

/**
 * A collapsed "Capabilities" section: one row per capability flag with a
 * three-way choice, "Default" (follow the model family), On or Off. Shared
 * by the Bedrock catalog dialog and the org own-provider model editor.
 */
export function CapabilityOverridesEditor({
  value,
  onChange,
  effective,
  family,
  triggerLabel = "Capabilities",
  disabled = false,
}: CapabilityOverridesEditorProps) {
  return (
    <Collapsible.Root className={styles.root}>
      <Collapsible.Trigger aria-label={triggerLabel}>
        Capabilities
      </Collapsible.Trigger>
      <Collapsible.Panel>
        <div className={styles.panel}>
          {family !== undefined && (
            <p className={styles.family}>Model family: {family ?? "unknown"}</p>
          )}
          {CAPABILITY_FLAGS.map((flag) => (
            <CapabilityRow
              key={flag}
              flag={flag}
              state={capabilityState(value, flag)}
              defaultLabel={defaultOptionLabel({
                flag,
                overrides: value,
                effective,
                family: family ?? null,
              })}
              onChange={(state) =>
                onChange(withCapabilityState(value, flag, state))
              }
              disabled={disabled}
            />
          ))}
        </div>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}

function CapabilityRow({
  flag,
  state,
  defaultLabel,
  onChange,
  disabled,
}: {
  flag: ModelCapabilityName;
  state: CapabilityState;
  defaultLabel: string;
  onChange: (state: CapabilityState) => void;
  disabled: boolean;
}) {
  return (
    <div className={styles.row}>
      <span className={styles.flagLabel}>{CAPABILITY_LABELS[flag]}</span>
      <ToggleGroup<CapabilityState>
        size="sm"
        aria-label={CAPABILITY_LABELS[flag]}
        value={[state]}
        onValueChange={([next]) => {
          // Pressing the current option again would clear the group; ignore it.
          if (next !== undefined) {
            onChange(next);
          }
        }}
        disabled={disabled}
      >
        <Toggle<CapabilityState> value="default">{defaultLabel}</Toggle>
        <Toggle<CapabilityState> value="on">On</Toggle>
        <Toggle<CapabilityState> value="off">Off</Toggle>
      </ToggleGroup>
    </div>
  );
}
